import { useState, useEffect } from 'react';
import {
    Box, Typography, IconButton, Button, TextField, Switch, Alert,
    ToggleButtonGroup, ToggleButton, Drawer, Select, MenuItem,
    FormControl, InputLabel, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ACCENT = '#427898';

const ABSENCE_TYPES = [
    { value: 'VACATION',     label: 'Vacation' },
    { value: 'SICK',         label: 'Sick Leave' },
    { value: 'COMPENSATION', label: 'Compensation (Time Off in Lieu)' },
    { value: 'MILITARY',     label: 'Military Service' },
    { value: 'CIVIL_SERVICE',label: 'Civil Service' },
    { value: 'MATERNITY',    label: 'Maternity Leave' },
    { value: 'PATERNITY',    label: 'Paternity Leave' },
    { value: 'PARENTAL',     label: 'Parental Leave' },
    { value: 'BEREAVEMENT',  label: 'Bereavement' },
    { value: 'MARRIAGE',     label: 'Marriage / Civil Union' },
    { value: 'UNPAID',       label: 'Unpaid Leave' },
    { value: 'OTHER',        label: 'Other' },
];

const toDateStr = (d) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

function countWeekdays(start, end) {
    if (!start || !end) return 0;
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end);   e.setHours(0, 0, 0, 0);
    if (e < s) return 0;
    let count = 0;
    const d = new Date(s);
    while (d <= e) {
        const day = d.getDay();
        if (day !== 0 && day !== 6) count++;
        d.setDate(d.getDate() + 1);
    }
    return count;
}

// employees: optional array of { id, firstName, lastName } — when provided shows admin employee picker
// submitError: optional error string to display in the footer (for employee self-service)
export default function AbsenceRequestDrawer({ open, onClose, onSave, overlappingNames = [], saving, employees = [], submitError = null }) {
    const isAdmin = employees.length > 0;

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [type, setType]                         = useState('VACATION');
    const [startDate, setStartDate]               = useState(new Date());
    const [endDate, setEndDate]                   = useState(new Date());
    const [halfDay, setHalfDay]                   = useState(false);
    const [halfDayPeriod, setHalfDayPeriod]       = useState('MORNING');
    const [reason, setReason]                     = useState('');

    useEffect(() => {
        if (open) {
            setSelectedEmployee(null);
            setType('VACATION');
            setStartDate(new Date());
            setEndDate(new Date());
            setHalfDay(false);
            setHalfDayPeriod('MORNING');
            setReason('');
        }
    }, [open]);

    useEffect(() => {
        if (halfDay) setEndDate(startDate);
    }, [halfDay, startDate]);

    const workingDays = halfDay ? 0.5 : countWeekdays(startDate, endDate);

    const handleSave = () => {
        onSave({
            type,
            startDate: toDateStr(startDate),
            endDate: toDateStr(halfDay ? startDate : endDate),
            halfDay,
            halfDayPeriod: halfDay ? halfDayPeriod : null,
            reason: reason.trim() || null,
            employeeId: selectedEmployee?.id ?? null,
        });
    };

    const isValid = type && startDate && (halfDay || (endDate && endDate >= startDate))
        && (!isAdmin || selectedEmployee);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100vw', sm: 480 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
            slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ height: 3, bgcolor: ACCENT, flexShrink: 0 }} />

                {/* Header */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography fontWeight={700} fontSize={16} color="#0F172A">
                            {isAdmin ? 'Add Absence' : 'Request Time Off'}
                        </Typography>
                        <Typography fontSize={12} color="#94A3B8" mt={0.3}>
                            {isAdmin ? 'Record an absence for an employee' : 'Submit an absence request'}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                    {/* Employee picker — admin only */}
                    {isAdmin && (
                        <Autocomplete
                            options={employees}
                            getOptionLabel={e => `${e.firstName} ${e.lastName}`}
                            value={selectedEmployee}
                            onChange={(_, v) => setSelectedEmployee(v)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            size="small"
                            renderInput={params => (
                                <TextField {...params} label="Employee" required />
                            )}
                        />
                    )}

                    {/* Type */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Absence type</InputLabel>
                        <Select value={type} onChange={e => setType(e.target.value)} label="Absence type">
                            {ABSENCE_TYPES.map(t => (
                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Half day toggle */}
                    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>Half day</Typography>
                            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Takes 0.5 days from the balance</Typography>
                        </Box>
                        <Switch checked={halfDay} onChange={e => setHalfDay(e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
                    </Box>

                    {/* Half day period */}
                    {halfDay && (
                        <ToggleButtonGroup
                            value={halfDayPeriod} exclusive
                            onChange={(_, v) => { if (v) setHalfDayPeriod(v); }}
                            size="small" fullWidth
                        >
                            <ToggleButton value="MORNING" sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}>
                                Morning
                            </ToggleButton>
                            <ToggleButton value="AFTERNOON" sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}>
                                Afternoon
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {/* Date(s) */}
                    {halfDay ? (
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', mb: 0.75 }}>Date</Typography>
                            <DatePicker
                                selected={startDate}
                                onChange={d => { setStartDate(d); setEndDate(d); }}
                                dateFormat="dd.MM.yyyy"
                                popperProps={{ strategy: 'fixed' }}
                                customInput={<TextField size="small" fullWidth />}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', mb: 0.75 }}>From</Typography>
                                <DatePicker
                                    selected={startDate}
                                    onChange={d => { setStartDate(d); if (endDate < d) setEndDate(d); }}
                                    dateFormat="dd.MM.yyyy"
                                    popperProps={{ strategy: 'fixed' }}
                                    customInput={<TextField size="small" fullWidth />}
                                />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', mb: 0.75 }}>To</Typography>
                                <DatePicker
                                    selected={endDate}
                                    minDate={startDate}
                                    onChange={setEndDate}
                                    dateFormat="dd.MM.yyyy"
                                    popperProps={{ strategy: 'fixed' }}
                                    customInput={<TextField size="small" fullWidth />}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Working days count */}
                    {startDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#F0F9FF', border: '1px solid #BFDBFE' }}>
                            <CalendarTodayIcon sx={{ fontSize: 14, color: '#2563EB' }} />
                            <Typography sx={{ fontSize: 13, color: '#1D4ED8', fontWeight: 600 }}>
                                {workingDays} working day{workingDays === 1 ? '' : 's'}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#60A5FA', ml: 0.5 }}>
                                (Mon – Fri, excl. public holidays)
                            </Typography>
                        </Box>
                    )}

                    {/* Reason */}
                    <TextField
                        label="Reason (optional)"
                        multiline rows={3} size="small" fullWidth
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder={type === 'SICK' ? 'Brief description (optional)' : 'e.g. family vacation, personal appointment'}
                    />

                    {/* Overlap warning */}
                    {overlappingNames.length > 0 && (
                        <Alert severity="warning" sx={{ fontSize: 12 }}>
                            <strong>Note:</strong> {overlappingNames.length === 1
                                ? `${overlappingNames[0]} is also absent during this period.`
                                : `${overlappingNames.slice(0, -1).join(', ')} and ${overlappingNames.slice(-1)} are also absent during this period.`}
                        </Alert>
                    )}
                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC' }}>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
                            {submitError}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={!isValid || saving}
                            onClick={handleSave}
                            sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                        >
                            {saving ? 'Saving…' : isAdmin ? 'Add Absence' : 'Submit Request'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
}
